/**
 * Canonical Custom Agents routes (CUSTOM_AGENTS_GEMINI_TRANSITION_REPORT.md §11).
 *
 * One entry per page that renders under /custom-agents today, paired with the
 * legacy route it mirrors. The legacy routes stay primary until the navigation
 * release: nav highlighting, the header filter, Axon page context and the
 * flag-off fallback all resolve a canonical URL through this table, so a page
 * behaves the same at either address. Add an entry when a later phase moves a
 * page rather than scattering /custom-agents literals.
 */
export const CUSTOM_AGENTS_BASE = '/custom-agents';

export const CUSTOM_AGENTS_ROUTES = Object.freeze([
  // The Dashboard is its own page, not an alias of Observability. `standalone`:
  // it is routed outside the canonical-route gate and renders whatever the
  // CUSTOM_AGENTS_ROUTES flag says, so links always use its canonical URL. The
  // fallback is only what the gate would use if it ever handled this path.
  { id: 'dashboard', path: `${CUSTOM_AGENTS_BASE}/dashboard`, fallbackPath: '/observability', standalone: true },
  { id: 'agent-network', path: `${CUSTOM_AGENTS_BASE}/agent-network`, legacyPath: '/agent-network' },
  { id: 'registry', path: `${CUSTOM_AGENTS_BASE}/registry`, legacyPath: '/admin/agents' },
  { id: 'users', path: `${CUSTOM_AGENTS_BASE}/users`, legacyPath: '/user-behavior' },
  // FinOps is the Gemini Enterprise Usage & ROI page (Usage & ROI | Tokenomics), 2026-10-04.
  // It mirrors the legacy Usage & ROI URL; the old canonical /custom-agents/usage-roi forwards here.
  { id: 'finops', path: `${CUSTOM_AGENTS_BASE}/finops`, legacyPath: '/finops/usage-roi' },
  { id: 'usage-roi', path: `${CUSTOM_AGENTS_BASE}/usage-roi`, legacyPath: '/finops/usage-roi' },
  // Conversations is the Atlas Conversations page pinned to custom (sessions +
  // Trace Viewer tab), not a mirror of /conversation-explorer, which stays the
  // trace explorer that other pages deep-link into (?user_id=, ?q=). The nav
  // links this canonical URL; the fallback is for the rollout flag being off.
  { id: 'conversations', path: `${CUSTOM_AGENTS_BASE}/conversations`, fallbackPath: '/conversation-explorer' },
  // Knowledge Gaps is the Atlas page pinned to custom; it has no legacy page, so the nav links
  // this canonical URL and the fallback (rollout flag off) is the Atlas page itself.
  { id: 'knowledge-gaps', path: `${CUSTOM_AGENTS_BASE}/knowledge-gaps`, fallbackPath: '/agent-atlas/adoption/knowledge-gaps' },
  { id: 'intent-analysis', path: `${CUSTOM_AGENTS_BASE}/intent-analysis`, legacyPath: '/intent-analysis' },
  // These pages reuse backed Custom data while remaining distinct destinations
  // from the original Threat Intelligence and Data Security tools. The fallback
  // is only used while the canonical-route rollout flag is disabled.
  { id: 'sources', path: `${CUSTOM_AGENTS_BASE}/sources`, fallbackPath: '/security/threat-intelligence' },
  { id: 'alerts', path: `${CUSTOM_AGENTS_BASE}/alerts`, legacyPath: '/observability/alerts' },
  // No legacy path: the engine had no Custom module until 2026-10-03, so this
  // page has never lived anywhere else.
  { id: 'recommendations', path: `${CUSTOM_AGENTS_BASE}/recommendations` },
  // Threats Control is no longer part of Custom Agents: its old canonical URL forwards
  // to the Security Overview (routes), and nothing in the module links to it.
  // Gemini-style security operations are real, surface-scoped Atlas pages.
  // Their fallbacks preserve useful destinations while the canonical route
  // flag is off, but they are not aliases of the original Custom tools.
  // Overview, Data Security and AI-BOM are the Security Atlas pages pinned to `otel`.
  { id: 'security-overview', path: `${CUSTOM_AGENTS_BASE}/security/overview`, fallbackPath: '/security-atlas/overview' },
  { id: 'data-security', path: `${CUSTOM_AGENTS_BASE}/security/data-security`, fallbackPath: '/security/data-security' },
  { id: 'ai-bom', path: `${CUSTOM_AGENTS_BASE}/security/ai-bom`, fallbackPath: '/security-atlas/ai-bom' },
  { id: 'signals', path: `${CUSTOM_AGENTS_BASE}/security/signals`, fallbackPath: '/security-atlas/signals' },
  { id: 'hunt', path: `${CUSTOM_AGENTS_BASE}/security/hunt`, fallbackPath: '/global-search' },
  { id: 'incidents', path: `${CUSTOM_AGENTS_BASE}/security/incidents`, fallbackPath: '/security/attack-path' },
  { id: 'skills', path: `${CUSTOM_AGENTS_BASE}/security/skills`, fallbackPath: '/red-team-scanner' },
  { id: 'response', path: `${CUSTOM_AGENTS_BASE}/security/response`, fallbackPath: '/security-atlas/response' },
  { id: 'exceptions', path: `${CUSTOM_AGENTS_BASE}/security/exceptions`, fallbackPath: '/security-atlas/exceptions' },
  { id: 'access-management', path: `${CUSTOM_AGENTS_BASE}/posture`, fallbackPath: '/security/data-security' },
  // Assessment: the Atlas Assessment pages pinned to Custom Agents. The legacy tool URLs
  // forward here (LegacyAssessmentRoute), and this mapping keeps their nav rows lit and their
  // Axon page context; the scan, report and evaluation screens beneath have no exact mirror.
  // The Overview has no legacy page; with the rollout flag off it falls back to the Red Team tool.
  { id: 'assessment', path: `${CUSTOM_AGENTS_BASE}/assessment`, fallbackPath: '/red-team-scanner' },
  { id: 'red-team', path: `${CUSTOM_AGENTS_BASE}/assessment/red-team`, legacyPath: '/red-team-scanner' },
  { id: 'scheduled-scans', path: `${CUSTOM_AGENTS_BASE}/assessment/schedules`, legacyPath: '/red-team/scheduled-scans' },
  { id: 'model-evaluation', path: `${CUSTOM_AGENTS_BASE}/assessment/evaluations`, legacyPath: '/model-evaluation/projects' },
  { id: 'compliance', path: `${CUSTOM_AGENTS_BASE}/compliance`, legacyPath: '/admin/compliance' },
  { id: 'audit-log', path: `${CUSTOM_AGENTS_BASE}/audit-log`, legacyPath: '/security/audit-log' },
  { id: 'policy', path: `${CUSTOM_AGENTS_BASE}/policy`, legacyPath: '/admin/policies' },
  { id: 'settings', path: `${CUSTOM_AGENTS_BASE}/settings`, legacyPath: '/finops/settings' },
]);

const stripTrailingSlash = (pathname) => (
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
);

/**
 * The legacy route a canonical Custom Agents URL mirrors, or null when the path
 * is not one. Exact match only: none of these pages has nested routes, and a
 * prefix match would map /custom-agents/dashboard/alerts onto the unrelated
 * /observability/alerts page.
 */
export function legacyPathFor(pathname) {
  if (!pathname) return null;
  const path = stripTrailingSlash(pathname);
  const route = CUSTOM_AGENTS_ROUTES.find((r) => r.path === path);
  return route?.legacyPath ?? null;
}

/** Safe destination used only when the canonical route rollout is disabled. */
export function fallbackPathFor(pathname) {
  if (!pathname) return null;
  const path = stripTrailingSlash(pathname);
  const route = CUSTOM_AGENTS_ROUTES.find((r) => r.path === path);
  return route ? (route.legacyPath ?? route.fallbackPath ?? null) : null;
}

/** `pathname`, with a canonical Custom Agents URL rewritten to its legacy route. */
export function toLegacyPath(pathname) {
  return legacyPathFor(pathname) ?? pathname;
}

/**
 * Model evaluations were a tab of the Agent Registry (?tab=evaluations on /custom-agents/registry
 * and /admin/agents); they are now their own section, Assessment → Model Evaluation. Where such
 * a URL now lives — its other query params and hash kept — or null when it is not one.
 */
export const EVALUATIONS_SECTION = `${CUSTOM_AGENTS_BASE}/assessment/evaluations`;
export function evaluationsTabTarget({ search = '', hash = '' } = {}) {
  const params = new URLSearchParams(search);
  if (params.get('tab') !== 'evaluations') return null;
  params.delete('tab');
  const q = params.toString();
  return `${EVALUATIONS_SECTION}${q ? `?${q}` : ''}${hash || ''}`;
}

/**
 * Resolve a Custom Agents sibling without leaking out of the canonical route
 * family. Legacy pages keep their legacy links until the navigation release.
 */
export function customAgentsPathFor(id, currentPathname = '') {
  const route = CUSTOM_AGENTS_ROUTES.find((candidate) => candidate.id === id);
  if (!route) return null;
  // A standalone page is served at its canonical URL whatever the rollout flag says.
  if (route.standalone) return route.path;
  return String(currentPathname).startsWith(`${CUSTOM_AGENTS_BASE}/`)
    ? route.path
    : (route.legacyPath ?? route.fallbackPath ?? null);
}
